import { observer } from "mobx-react-lite";
import { FC } from "react";
import styled from "@emotion/styled";
import { PlatformIconKind } from "@/domains/api/types";
import { AppCalloutBannerIcon } from "@/components/banners/app-callout-banner/app-callout-banner-icon";
import { AppCalloutBannerPrimaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-primary-label";
import { AppCalloutBannerSecondaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-secondary-label";
import { AppCalloutBannerActionButton } from "@/components/banners/app-callout-banner/app-callout-banner-action-button";
import { MDSIconKind } from "@/mds";
import { MDSIconButton } from "@/mds";
import { AppCalloutBannerSlimPrimaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-slim-primary-label";

interface AppCalloutBannerProps {
  iconKind: PlatformIconKind | null;
  isDismissible: boolean;
  primaryLabel: string;
  secondaryLabel: string;
  inlineActionLabel: string | null;
  inlineActionUrl: string | null;
  primaryActionLabel: string | null;
  primaryActionUrl: string | null;
  primaryActionIconKind: PlatformIconKind | null;
  secondaryActionLabel: string | null;
  secondaryActionUrl: string | null;
  secondaryActionIconKind: PlatformIconKind | null;
  tertiaryActionLabel: string | null;
  tertiaryActionUrl: string | null;
  tertiaryActionIconKind: PlatformIconKind | null;
  handleDismiss: () => Promise<void>;
}

export const AppCalloutBannerContent: FC<AppCalloutBannerProps> = observer(
  ({
    iconKind,
    isDismissible,
    primaryLabel,
    secondaryLabel,
    inlineActionLabel,
    inlineActionUrl,
    primaryActionLabel,
    primaryActionUrl,
    primaryActionIconKind,
    secondaryActionLabel,
    secondaryActionUrl,
    secondaryActionIconKind,
    tertiaryActionLabel,
    tertiaryActionUrl,
    tertiaryActionIconKind,
    handleDismiss,
  }) => {
    if (!iconKind && !secondaryLabel && !inlineActionLabel && !inlineActionUrl) {
      return (
        <SlimLayoutContent
          isDismissible={isDismissible}
          primaryLabel={primaryLabel}
          primaryActionLabel={primaryActionLabel}
          primaryActionUrl={primaryActionUrl}
          primaryActionIconKind={primaryActionIconKind}
          secondaryActionLabel={secondaryActionLabel}
          secondaryActionUrl={secondaryActionUrl}
          secondaryActionIconKind={secondaryActionIconKind}
          tertiaryActionLabel={tertiaryActionLabel}
          tertiaryActionUrl={tertiaryActionUrl}
          tertiaryActionIconKind={tertiaryActionIconKind}
          handleDismiss={handleDismiss}
        />
      );
    }

    return (
      <Wrapper>
        <InnerWrapper>
          <LeftSection>
            <AppCalloutBannerIcon iconKind={iconKind} />
            <ContentSection>
              <AppCalloutBannerPrimaryLabel label={primaryLabel} />
              <AppCalloutBannerSecondaryLabel
                label={secondaryLabel}
                inlineActionLabel={inlineActionLabel}
                inlineActionUrl={inlineActionUrl}
              />
              <ActionButtons>
                <AppCalloutBannerActionButton
                  label={primaryActionLabel}
                  url={primaryActionUrl}
                  iconKind={primaryActionIconKind}
                />
                <AppCalloutBannerActionButton
                  label={secondaryActionLabel}
                  url={secondaryActionUrl}
                  iconKind={secondaryActionIconKind}
                />
                <AppCalloutBannerActionButton
                  label={tertiaryActionLabel}
                  url={tertiaryActionUrl}
                  iconKind={tertiaryActionIconKind}
                />
              </ActionButtons>
            </ContentSection>
          </LeftSection>
          <RightSection>
            {isDismissible && <DismissButton iconKind={MDSIconKind.Exit} onClick={handleDismiss} />}
          </RightSection>
        </InnerWrapper>
      </Wrapper>
    );
  }
);

const SlimLayoutContent: FC<
  Omit<
    AppCalloutBannerProps,
    "iconKind" | "secondaryLabel" | "inlineActionLabel" | "inlineActionUrl"
  >
> = observer(
  ({
    isDismissible,
    primaryLabel,
    primaryActionLabel,
    primaryActionUrl,
    primaryActionIconKind,
    secondaryActionLabel,
    secondaryActionUrl,
    secondaryActionIconKind,
    tertiaryActionLabel,
    tertiaryActionUrl,
    tertiaryActionIconKind,
    handleDismiss,
  }) => {
    return (
      <Wrapper>
        <SlimInnerWrapper>
          <LeftSection>
            <SlimContentSection>
              <AppCalloutBannerSlimPrimaryLabel label={primaryLabel} />
            </SlimContentSection>
          </LeftSection>
          <SlimRightSection>
            <SlimActionButtons>
              <AppCalloutBannerActionButton
                label={primaryActionLabel}
                url={primaryActionUrl}
                iconKind={primaryActionIconKind}
              />
              <AppCalloutBannerActionButton
                label={secondaryActionLabel}
                url={secondaryActionUrl}
                iconKind={secondaryActionIconKind}
              />
              <AppCalloutBannerActionButton
                label={tertiaryActionLabel}
                url={tertiaryActionUrl}
                iconKind={tertiaryActionIconKind}
              />
            </SlimActionButtons>
            {isDismissible && <DismissButton iconKind={MDSIconKind.Exit} onClick={handleDismiss} />}
          </SlimRightSection>
        </SlimInnerWrapper>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.colors.background.banner.emphasis,
  display: "flex",
  flexDirection: "column",
  containerName: "appCalloutBanner",
}));

const InnerWrapper = styled.div({
  flexWrap: "nowrap",
  borderRadius: "12px",
  height: "100%",
  width: "100%",
  minWidth: "320px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexGrow: 1,
  containerType: "inline-size",
  containerName: "appCalloutBanner",
  paddingLeft: "12px",
  paddingRight: "12px",
});

const LeftSection = styled.div({
  flexWrap: "nowrap",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  paddingLeft: "16px",
  paddingTop: "16px",
  "@container appCalloutBanner (min-width: 440px)": {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: "0px",
  },
});

const ContentSection = styled.div({
  paddingTop: "0px",
  paddingBottom: "24px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  "@container appCalloutBanner (min-width: 440px)": {
    paddingTop: "24px",
  },
});

const RightSection = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
});

const ActionButtons = styled.div({
  flexWrap: "wrap",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "8px",
});

const DismissButton = styled(MDSIconButton)({
  margin: "8px",
});

const SlimInnerWrapper = styled.div({
  flexWrap: "nowrap",
  borderRadius: "12px",
  height: "100%",
  width: "100%",
  minWidth: "300px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  flexGrow: 1,
  containerType: "inline-size",
  containerName: "appCalloutBanner",
  paddingLeft: "12px",
  paddingRight: "12px",
});

const SlimContentSection = styled.div({
  paddingTop: "0px",
  paddingBottom: "24px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "8px",
  "@container appCalloutBanner (min-width: 440px)": {
    paddingTop: "24px",
  },
});

const SlimRightSection = styled.div({
  height: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "8px",
});

const SlimActionButtons = styled.div({
  flexWrap: "wrap",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "8px",
});
